import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { InfoSmallIcon, Select, SelectTrigger, Tooltip } from "@activecollab/components";
import PropTypes from "prop-types";

const SelectContainer = ({
  options,
  selected,
  onChange,
  placeholder,
  className,
  defaultValue,
  defaultSelectedName,
  fieldRef,
  invalid,
  errorMessage,
  ...rest
}) => {
  const { t } = useTranslation();
  const selectedName = useMemo(() => {
    const result = options.find(({ id }) => id === selected)?.name;
    if (!result && defaultSelectedName) {
      return defaultSelectedName;
    }
    return result;
  }, [options, selected, defaultSelectedName]);

  return (
    <Select
      target={
        <SelectTrigger
          className={className}
          invalid={invalid}
          typographyProps={{ color: selectedName ? "primary" : "quaternary" }}
          ref={fieldRef}
          endAdornment={
            invalid ? (
              <Tooltip title={errorMessage}>
                <InfoSmallIcon
                  fill="var(--red-alert)"
                  className="tw-cursor-help tw-mr-1"
                />
              </Tooltip>
            ) : null
          }
        >
          {selectedName ?? placeholder}
        </SelectTrigger>
      }
      defaultValue={defaultValue}
      noResultText={t("No result")}
      selected={selected > 0 ? selected : undefined}
      options={options}
      onChange={onChange}
      forceCloseMenu
      disabledInternalSort
      {...rest}
    />
  );
};

SelectContainer.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      image: PropTypes.string,
    })
  ).isRequired,
  selected: PropTypes.number,
  onChange: PropTypes.func.isRequired,
  defaultValue: PropTypes.string,
  placeholder: PropTypes.string,
  className: PropTypes.string,
  defaultSelectedName: PropTypes.string,
  fieldRef: PropTypes.func,
  invalid: PropTypes.bool,
  errorMessage: PropTypes.string,
};

SelectContainer.defaultProps = {
  invalid: false,
};

export default SelectContainer;
